import { action } from "typesafe-actions";
import {
  IBusinessPolicyUnderWriting,
  defaultBusinessPolicyUnderWritingState,
} from "./businessPolicyUnderWriting.types";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../../initialStoreState";
import { AnyAction } from "redux";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../../messages/messagesActions";
import { api } from "../../../api/api";
import {
  BinderScopeArg,
  buildBinderRequest,
} from "../leadBinder/leadBinderEndpoints";

// Feature: lead-binder-business-tabs
// A 30-second bound is applied to lead-scoped fetch/save requests so the error +
// retry path (Requirements 3.5, 3.6) triggers deterministically on a stalled request.
const LEAD_BINDER_REQUEST_TIMEOUT_MS = 30000;

// Feature: lead-binder-business-tabs
// Guard shared across the module so an in-flight lead-scoped save cannot be
// duplicated for the same lead before it resolves (Requirement 3.8). The
// customer-policy path is unaffected: it never sets or reads this guard.
let leadBinderUnderwritingSaveInFlight = false;

export const FETCH_BUSINESS_POLICY_UNDER_WRITING_PROGRESS =
  "FETCH_BUSINESS_POLICY_UNDER_WRITING_PROGRESS";
export const FETCH_BUSINESS_POLICY_UNDER_WRITING_SUCCESS =
  "FETCH_BUSINESS_POLICY_UNDER_WRITING_SUCCESS";
export const FETCH_BUSINESS_POLICY_UNDER_WRITING_FAILED =
  "FETCH_BUSINESS_POLICY_UNDER_WRITING_FAILED";

export const fetchBusinessUnderWritingProgress = () =>
  action(FETCH_BUSINESS_POLICY_UNDER_WRITING_PROGRESS);
export const fetchBusineUnderWritingSuccess = (
  data: IBusinessPolicyUnderWriting
) => action(FETCH_BUSINESS_POLICY_UNDER_WRITING_SUCCESS, { data });
export const fetchBusinessUnderWritingfailed = () =>
  action(FETCH_BUSINESS_POLICY_UNDER_WRITING_FAILED);

export const fetchBusinessUnderWritingAsync =
  (
    customerPolicyId: number,
    leadId?: string | null,
    onError?: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessUnderWritingProgress());

      // Feature: lead-binder-business-tabs
      // Branch on a supplied lead_id (mirroring the leadAutoApplicationActions /
      // automobileApplicationActions precedent). In lead scope the request is keyed
      // by lead_id and carries no customer_policy_id; otherwise the original
      // customer-policy request is issued byte-for-byte unchanged (Requirement 4.2).
      const scope: BinderScopeArg = leadId
        ? { kind: "lead", leadId }
        : { kind: "policy", customerPolicyId };
      const request = buildBinderRequest("underwriting", scope, "fetch");

      const res = await api.get(
        request.url,
        leadId ? { timeout: LEAD_BINDER_REQUEST_TIMEOUT_MS } : undefined
      );
      const data: IBusinessPolicyUnderWriting[] = res.data.data;
      if (data.length > 0) {
        dispatch(fetchBusineUnderWritingSuccess(data[0]));
      } else {
        dispatch(
          fetchBusineUnderWritingSuccess(
            defaultBusinessPolicyUnderWritingState["data"]
          )
        );
      }
    } catch (err: any) {
      dispatch(fetchBusinessUnderWritingfailed());
      dispatch(
        showMessage({
          type: "error",
          message: leadId
            ? "The Underwriting binder could not be loaded. Please retry."
            : err.response.data.message,
          displayAs: "snackbar",
        })
      );
      // Feature: lead-binder-business-tabs
      // Surface the failure to the caller so the sub-tab can expose an inline retry
      // control and retain any unsaved broker input (Requirements 1.7, 3.5).
      if (onError) {
        onError();
      }
    }
  };

export const addBusinessUnderWritingAsync =
  (
    underwriting: IBusinessPolicyUnderWriting,
    onSuccess: () => void,
    leadId?: string | null,
    onError?: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    // Feature: lead-binder-business-tabs
    // Prevent a duplicate lead-scoped save for the same record while one is already
    // in flight (Requirement 3.8). The customer-policy path never enters this guard.
    if (leadId && leadBinderUnderwritingSaveInFlight) {
      return;
    }
    try {
      if (leadId) {
        leadBinderUnderwritingSaveInFlight = true;
      }
      dispatch(saveLoaderProgress());

      if (leadId) {
        // Lead scope: persist with lead_id and NO customer_policy_id / customer_id.
        const request = buildBinderRequest(
          "underwriting",
          { kind: "lead", leadId },
          "save"
        );
        const { customer_policy_id, customer_id, ...rest } = underwriting;
        await api.post(
          request.url,
          { ...rest, ...request.body },
          { timeout: LEAD_BINDER_REQUEST_TIMEOUT_MS }
        );
        dispatch(
          showMessage({
            type: "success",
            message: "Underwriting saved successfully!",
            displayAs: "snackbar",
          })
        );
        // Re-fetch by lead_id after a successful save (Requirements 3.3, 3.7).
        dispatch(fetchBusinessUnderWritingAsync(0, leadId));
        onSuccess();
      } else {
        // Customer-policy scope: unchanged behavior (Requirement 4.2).
        await api.post(`/policy/edit-policy-business-underwriting`, {
          ...underwriting,
        });
        dispatch(
          showMessage({
            type: "success",
            message: "Underwriting saved successfully!",
            displayAs: "snackbar",
          })
        );
        dispatch(fetchBusineUnderWritingSuccess(underwriting));
        onSuccess();
      }
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: leadId
            ? "Failed to save Underwriting binder. Your changes were not discarded — please retry."
            : err.response.data.message,
          displayAs: "snackbar",
        })
      );
      // Feature: lead-binder-business-tabs
      // Report the save failure so the sub-tab retains the broker's entered input
      // without discarding it and can re-submit (Requirement 3.6).
      if (onError) {
        onError();
      }
    } finally {
      if (leadId) {
        leadBinderUnderwritingSaveInFlight = false;
      }
      dispatch(saveLoaderCompleted());
    }
  };

export const CLEAR_BUSINESS_POLICY_UNDER_WRITING =
  "CLEAR_BUSINESS_POLICY_UNDER_WRITING";

export const clearBusinessPolicyUnderWritingState = () =>
  action(CLEAR_BUSINESS_POLICY_UNDER_WRITING);
